import { action, observable, runInAction, makeObservable } from 'mobx';
import { UserCreateForm } from 'modules/user/forms';
import { BaseViewStore } from 'core/stores';
import { formInvalidate, isSuperAdminOrganization } from 'core/utils';
import { PrintNodeInfoStore } from 'modules/print-node-info/stores';
import { notificationError, generateRandomString } from 'core/utils';
import { CoreUserInfoStore } from 'modules/core-user-info/stores';
import { warehouseRoles, lookupRouteName, sellerRoles, roles as allRoles } from 'common/constants';
import { LookupStore } from 'modules/lookup/stores';
import _ from 'lodash';
import { BaasicDropdownViewStore } from 'common/stores';
import { localStorageHandler } from 'core/services';
import { ApplicationStore } from 'platform/application/stores';
import UserStore from './UserStore';

class UserCreateViewStore extends BaseViewStore {
	@observable showNotification = false;
	@observable hasPiiAccess = false;
	@observable canSetPii = false;
	@observable enableEmailNotification = false;
	@observable canSetEnableEmailNotification = false;
	notification = null;
	@observable isHavingAPISecret = false;
	loggedUser = null;
	timeouts = [];

	form = new UserCreateForm({
		onSuccess: async () => {
			const userPhone = this.form.$('userPhone').value;
			if (!this.checkUserPhone() && userPhone) {
				this.form.$('userPhone').invalidate('LOGINFORM.INVALIDUSERPHONEFORMAT');
			} else {
				await this.createUser();
			}
		},
		onError: async () => {
			const userPhone = this.form.$('userPhone').value;
			if (!this.checkUserPhone() && userPhone) {
				this.form.$('userPhone').invalidate('LOGINFORM.INVALIDUSERPHONEFORMAT');
			}
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.loggedUser = rootStore.authStore.user;
		this.listStore = rootStore.userListViewStore.listStore;
		this.applicationStore = new ApplicationStore(rootStore);
		this.printNodeInfoStore = new PrintNodeInfoStore(rootStore);
		this.coreUserInfoStore = new CoreUserInfoStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.notification = 'LOGINFORM.PRINTNODE.NOTIFICATIONS.REQUIRED';
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action
	initialize = async () => {
		this.suspend();
		if (!this.isSuperAdmin) {
			this.userOrganizationInfo = await this.applicationStore.getOrganizationInfo();
		}
		await this.initializeDropdowns();
		const piiRoles = _.find(
			this.loggedUser.roles,
			role =>
				role == warehouseRoles.warehouseAccountOwner ||
				role == sellerRoles.sellerAccountOwner ||
				role == allRoles.scanshipSuperAdministrator ||
				role == allRoles.superAdministrators
		);
		this.canSetPii = piiRoles && this.rootStore.authStore.hasPiiAccess;
		this.canSetEnableEmailNotification =
			_.find(this.loggedUser.roles, role => role == warehouseRoles.warehouseAccountOwner) != null;
		this.resume();
	};

	@action.bound
	initializeDropdowns = async () => {
		this.roleDropdownStore = new BaasicDropdownViewStore();
		await this.roleDropdownStore.initialize({
			field: this.form.$('roleIds'),
			store: new UserStore(this.rootStore),
			fetchFunc: 'fetchRoles',
			fetchInitialValues: _.get(this.userOrganizationInfo, 'organizationType.name') != null,
			filterProperties: {
				organizationTypeName: _.get(this.userOrganizationInfo, 'organizationType.name') || null,
			},
			isMulti: true,
		});
		if (this.isSuperAdmin) {
			this.organizationDropdownStore = new BaasicDropdownViewStore();
			await this.organizationDropdownStore.initialize({
				field: this.form.$('organizationId'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					embed: ['organizationType'],
					page: 1,
					rpp: 100,
				},
				onChangeCallback: async value => {
					this.userOrganizationInfo = value.extra;
					await this.roleDropdownStore.updateFilter({ organizationTypeName: value.extra.organizationType.name });
				},
			});
			this.form.$('organizationId').set('rules', 'required');
			const organization = localStorageHandler.get('selectedOrganization');
			if (organization) {
				this.organizationDropdownStore.setValue(organization);
				this.userOrganizationInfo = this.organizationDropdownStore.value.extra;
			}
		}
		this.countryDropdownStore = new BaasicDropdownViewStore();
		await this.countryDropdownStore.initialize({
			field: this.form.$('countryId'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.countryCustom,
			filterCallback: options => {
				return options.map(option => {
					return {
						value: option.id,
						label: `${option.name} | ${option.phoneCode}`,
						extra: option,
					};
				});
			},
		});

		this.labelPrinterDropdownStore = new BaasicDropdownViewStore();
		await this.labelPrinterDropdownStore.initialize({
			field: this.form.$('labelPrinterId'),
			store: new PrintNodeInfoStore(this.rootStore),
			fetchFunc: 'getPrinters',
			fetchInitialValues: false,
		});

		this.packingSlipPrinterDropdownStore = new BaasicDropdownViewStore();
		await this.packingSlipPrinterDropdownStore.initialize({
			field: this.form.$('packingSlipPrinterId'),
			store: new PrintNodeInfoStore(this.rootStore),
			fetchFunc: 'getPrinters',
			fetchInitialValues: false,
		});
	};

	@action
	createUser = async () => {
		const { roleIds, ...fields } = this.form.values();

		const userRoles = _.map(this.roleDropdownStore.value, role => {
			return {
				name: role.label,
				id: role.value,
			};
		});

		let user = {
			isApproved: true,
			creationDate: new Date(),
			roles: userRoles,
			piiAccess: this.hasPiiAccess,
			apiSecret: fields.apiSecret,
			enableEmailNotification: this.enableEmailNotification,
			organizationId: this.isSuperAdmin ? this.form.$('organizationId').value : null,
			labelPrinterName: _.get(this.labelPrinterDropdownStore, 'value.label'),
			packingSlipPrinterName: _.get(this.packingSlipPrinterDropdownStore, 'value.label'),
			...fields,
			userPhone: _.get(this.countryDropdownStore, 'value.extra.phoneCode') + ' ' + this.form.$('userPhone').value,
		};

		delete user.organization;
		if (
			this.form
				.$('password')
				.value.toLowerCase()
				.includes(this.form.$('userName').value.toLowerCase())
		) {
			this.form.invalidate('PASSWORDCONTAINSUNSERNAME');
		} else {
			this.suspend();
			try {
				await this.listStore.createUser(user);
				this.rootStore.notificationStore.success(['LOOKUPCREATE.USER', { user: user.userName }]);
				this.modalParams.success();
			} catch (err) {
				formInvalidate({ form: this.form, err });
			}

			this.resume();
		}
	};

	@action.bound
	getPrinters = async () => {
		if (this.form.$('apiKey').value.trim() == '') {
			this.showNotification = true;
			return;
		}
		this.showNotification = false;
		await this.clearPrinters();
		if (this.labelPrinterDropdownStore.error) {
			this.handlePrinterError();
		} else if (this.labelPrinterDropdownStore.options.length > 0)
			this.rootStore.notificationStore.success('LOGINFORM.PRINTNODE.NOTIFICATIONS.PRINTERSFOUND');
		else this.rootStore.notificationStore.success('LOGINFORM.PRINTNODE.NOTIFICATIONS.PRINTERSNOTFOUND');
	};

	@action.bound
	handlePrinterError = () => {
		if (_.get(this.labelPrinterDropdownStore, 'error.data.isSuccess') === false) {
			this.rootStore.notificationStore.error('LOGINFORM.PRINTNODE.NOTIFICATIONS.INVALIDAPIKEY');
			this.labelPrinterDropdownStore.error = null;
		} else {
			notificationError({ store: this.rootStore.notificationStore, err: this.labelPrinterDropdownStore.error });
		}
	};

	@action.bound
	onCheckPiiAccess = props => {
		if (props) {
			this.hasPiiAccess = props.currentTarget.checked;
		}
	};

	@action.bound
	onCheckEnableEmailNotification = props => {
		if (props) {
			this.enableEmailNotification = props.currentTarget.checked;
		}
	};

	@action.bound
	clearPrinters = async () => {
		for (const dropdown of [this.labelPrinterDropdownStore, this.packingSlipPrinterDropdownStore]) {
			dropdown.setValue(null);
			await dropdown.updateFilter({ apiKey: this.form.$('apiKey').value });
		}
	};

	@action.bound generateApiSecret() {
		this.isHavingAPISecret = true;
		var radnomStringGenerated = generateRandomString(32);
		this.form.$('apiSecret').set('value', radnomStringGenerated);
	}

	onUserPhoneChange = value => {
		// Automatically correct user phone input 2.5 seconds after user stops typing
		const highestTimeoutId = setTimeout(() => {
			this.form.$('userPhone').resetValidation();
			this.timeouts.forEach(timeoutId => {
				if (timeoutId < highestTimeoutId) {
					clearTimeout(timeoutId);
				}
			});
			this.timeouts.push(
				setTimeout(() => {
					this.correctUserPhone();
				}, 2500)
			);
		}, 0);
	};

	@action.bound
	correctUserPhone = () => {
		const regex = /^\d+$/;
		let value = this.form.$('userPhone').value;
		if (!value.match(regex) && value) {
			value = value.replace(/\D/g, '');
			this.form.$('userPhone').value = value;
			return true;
		}
	};

	@action.bound
	checkUserPhone = () => {
		let value = this.form.$('userPhone').value;
		if (value) {
			const regex = /^\d+$/;
			var phone = _.get(this.countryDropdownStore, 'value.extra.phoneCode') + value;
			if ((!value.match(regex) && value.length > 0) || (phone.length < 7 || phone.length > 15)) {
				return false;
			} else {
				return true;
			}
		}
	};
}

export default UserCreateViewStore;



// WEBPACK FOOTER //
// ./src/modules/user/stores/UserCreateViewStore.js